import { useEffect, useRef, useState } from 'react';
import { invoicePayReturnPath, t, type PayReturnAnswer, type PayReturnState } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { INVOICES_PATH, get } from './api';

/** How often the return asks while the payment is being confirmed, and how many times before it says to reload later. */
export const PAY_RETURN_POLL_MS = 3_000;
export const PAY_RETURN_POLL_TRIES = 40;

/**
 * 🔒 The Checkout's return on the invoice page (decision 1650): being
 * confirmed while the processor says paid and no payment is recorded yet,
 * then the invoice reloaded and shown paid. A bank transfer still settling
 * reads that it is on its way, and a Checkout left unpaid that nothing was
 * charged.
 *
 * 🔨 It asks every few seconds for up to two minutes and then stops, saying
 * the customer need do nothing more; the webhook records the payment whether
 * or not this page is still open.
 */
export function PayReturnNotice({ invoiceId, session, gateway, apiBaseUrl = '', onPaid, pollMs = PAY_RETURN_POLL_MS }: {
  invoiceId: string;
  session: string;
  gateway: string;
  apiBaseUrl?: string;
  onPaid: () => void;
  pollMs?: number;
}) {
  const [state, setState] = useState<PayReturnState | 'unreadable' | null>(null);
  const [tries, setTries] = useState(0);
  const paid = useRef(onPaid);
  useEffect(() => {
    paid.current = onPaid;
  }, [onPaid]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const answer = await get<PayReturnAnswer>(invoicePayReturnPath(INVOICES_PATH, invoiceId, session, gateway), apiBaseUrl);
      if (cancelled) return;
      if (!answer.ok) {
        setState('unreadable');
        return;
      }
      setState(answer.data.state);
      if (answer.data.state === 'paid') paid.current();
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceId, session, gateway, apiBaseUrl, tries]);

  useEffect(() => {
    if (state !== 'confirming' || tries >= PAY_RETURN_POLL_TRIES) return undefined;
    const timer = setTimeout(() => setTries((n) => n + 1), pollMs);
    return () => clearTimeout(timer);
  }, [state, tries, pollMs]);

  const shown = state ?? 'confirming';
  const message =
    shown === 'paid' ? t('invoice.pay.paid')
    : shown === 'processing' ? t('invoice.pay.processing')
    : shown === 'notCompleted' ? t('invoice.pay.notCompleted')
    : shown === 'unreadable' ? t('invoice.pay.unreadable')
    : tries >= PAY_RETURN_POLL_TRIES ? t('invoice.pay.slow') : t('invoice.pay.confirming');
  return (
    <div data-invoice-pay-return={shown}>
      <AlertBanner tone={shown === 'paid' ? 'good' : 'info'} message={message} />
    </div>
  );
}
